import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import exifr from "exifr";
import { encodePhoto, photoDimensions } from "../../utils/photoQuality";
import Style from "./CustomCamera.module.css";
import PermissionHelpModal from "./PermissionHelpModal.jsx";
import { ehErroDePermissao } from "../../utils/permissoesApp";
// Abertura de câmera resiliente (exact → ideal → deviceId, com conferência
// do facingMode real). Ver src/utils/cameraStream.js.
import { abrirStream, facingDoStream, pararStream } from "../../utils/cameraStream";

// 1x1 GIF transparente — impede o browser de mostrar o ícone de play
const BLANK_POSTER = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// persiste thumbnail da galeria durante a sessão
let _lastGalleryThumb = null;

// O canvas remove EXIF; a data é lida do arquivo original antes do encode.
// DateTimeOriginal não tem fuso: o produto interpreta como America/Sao_Paulo.
// Ausente ou ilegível resulta em null.
const TZ_SP = "America/Sao_Paulo";

// Offset (ms) de America/Sao_Paulo num dado instante UTC — via Intl, para
// cobrir inclusive fotos antigas de quando o Brasil ainda tinha horário de verão.
const offsetSpEm = (utcMs) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ_SP, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = {};
  for (const { type, value } of dtf.formatToParts(new Date(utcMs))) p[type] = value;
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return asUtc - utcMs;
};

// Hora de parede SP → ISO com offset (ex.: "2026-08-30T12:41:07-03:00").
const paredeSpParaIso = (y, mo, d, h, mi, s) => {
  const pretendido = Date.UTC(y, mo - 1, d, h, mi, s);
  let utc = pretendido - offsetSpEm(pretendido);
  utc = pretendido - offsetSpEm(utc); // 2ª passada estabiliza em virada de DST
  const offMin = Math.round(offsetSpEm(utc) / 60000);
  const pad = (n) => String(n).padStart(2, "0");
  const off = `${offMin < 0 ? "-" : "+"}${pad(Math.floor(Math.abs(offMin) / 60))}:${pad(Math.abs(offMin) % 60)}`;
  return `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}:${pad(s)}${off}`;
};

// Lê DateTimeOriginal CRU ("YYYY:MM:DD HH:MM:SS") do arquivo original da
// galeria. reviveValues:false evita que a exifr converta usando o fuso do
// APARELHO (que pode não ser SP). Nunca rejeita: qualquer falha → null.
const lerTiradaEmDaGaleria = async (file) => {
  try {
    const exif = await exifr.parse(file, {
      pick: ["DateTimeOriginal"],
      reviveValues: false,
    });
    const bruto = exif?.DateTimeOriginal;
    if (typeof bruto !== "string") return null;
    const m = bruto.trim().match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (!m) return null;
    const [y, mo, d, h, mi, s] = m.slice(1).map(Number);
    // Placeholder "0000:00:00 00:00:00" e valores impossíveis → null.
    if (y < 1980 || mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 59) {
      return null;
    }
    return paredeSpParaIso(y, mo, d, h, mi, s);
  } catch {
    return null;
  }
};

// allowGallery=false: modo COMPETIÇÃO — a foto tem de ser capturada na hora
// (galeria escondida; a verificação do dono depende de foto ao vivo).
const CustomCamera = ({
  mode = "checkin",
  onCapture,
  onClose,
  initialStream = null,
  title = null,
  allowGallery = true,
  // Faixa opcional logo acima da barra de baixo, só no visor (some no
  // review). É por onde a experiência nova põe o seletor de modo
  // (Refeição · Treino · Post) sem duplicar câmera.
  slotModos = null,
  // Dica sob a moldura. Sem isto, a dica era fixa do ScanFood.
  dica = null,
  // Moldura de enquadramento (a do ScanFood). Por padrão segue o mode.
  comMoldura = null,
}) => {
  const videoRef        = useRef(null);
  const canvasRef       = useRef(null);
  const streamRef       = useRef(null);
  const galleryInputRef = useRef(null);
  // Cada pedido de câmera ganha um número; só o MAIS RECENTE pode assumir o
  // <video>. Sem isso, os dois efeitos de montagem disparavam dois
  // getUserMedia concorrentes e um dos streams ficava ÓRFÃO, segurando a
  // traseira para sempre — e com a traseira presa o pedido da frontal ou
  // falhava (Android abre uma câmera por vez) ou voltava a traseira.
  const pedidoRef       = useRef(0);
  // Câmera pedida no último start (o state `facingMode` guarda a que de fato
  // veio; para reabrir/retomar o que vale é a intenção).
  const facingRef       = useRef("environment");
  // Efeito de retake também roda na montagem — este flag faz ele ignorar essa
  // primeira passada, que já é do efeito de abertura.
  const puloPrimeiroRef = useRef(true);
  const pollProntoRef   = useRef(null);
  // Metadados da foto em preview: { origem: "camera"|"galeria", tiradaEm: string|null }.
  // Entregue como 2º argumento OPCIONAL do onCapture — retrocompatível: os
  // chamadores existentes que só leem o 1º arg continuam funcionando.
  const metaCapturaRef  = useRef(null);

  const [ready,         setReady]        = useState(false);
  const [facingMode,    setFacingMode]   = useState("environment");
  const [flash,         setFlash]        = useState(false);
  const [capturing,     setCapturing]    = useState(false);
  const [error,         setError]        = useState(null);
  const [preview,       setPreview]      = useState(null);
  const [galleryThumb,  setGalleryThumb] = useState(_lastGalleryThumb);
  // v3.7.3: permissão de câmera NEGADA — mostra o passo a passo da plataforma
  // + atalho para as configurações do app, em vez do "tente novamente" cego.
  const [permissaoNegada, setPermissaoNegada] = useState(false);

  const isScanFood = mode === "scanfood";

  // ── câmera ────────────────────────────────────────────────────────────────
  const pararPollPronto = useCallback(() => {
    if (pollProntoRef.current) {
      clearInterval(pollProntoRef.current);
      pollProntoRef.current = null;
    }
  }, []);

  // Liga o stream no <video> e destrava o obturador quando houver quadro.
  // No WKWebView (iOS) TROCAR o srcObject do mesmo <video> às vezes não dispara
  // loadedmetadata: o preview ficava congelado no último quadro da câmera
  // anterior e o obturador nunca reabilitava — exatamente o "a frontal não
  // funciona". Daí o reset com srcObject=null, os três eventos e o poll de
  // segurança em videoWidth (que só passa de 0 quando existe quadro real).
  const ligarNoVideo = useCallback((stream, pedido) => {
    const video = videoRef.current;
    if (!video) return;
    pararPollPronto();

    const marcarPronto = () => {
      if (pedido !== pedidoRef.current) return;
      pararPollPronto();
      setReady(true);
    };

    try { video.srcObject = null; } catch { /* WebView antiga */ }
    video.onloadedmetadata = marcarPronto;
    video.oncanplay        = marcarPronto;
    video.onplaying        = marcarPronto;
    video.srcObject = stream;
    // O play() pode rejeitar com AbortError quando um novo load interrompe o
    // anterior — é esperado durante a troca e não é erro para o usuário.
    Promise.resolve(video.play()).then(marcarPronto).catch(() => {});

    let tentativas = 0;
    pollProntoRef.current = setInterval(() => {
      if (pedido !== pedidoRef.current || ++tentativas > 40) return pararPollPronto();
      if (video.videoWidth > 0) marcarPronto();
    }, 100);
  }, [pararPollPronto]);

  const startCamera = useCallback(async (facing = "environment") => {
    const pedido = ++pedidoRef.current;
    facingRef.current = facing;
    pararPollPronto();
    setReady(false);
    setError(null);

    // Solta a câmera atual ANTES de pedir a outra: no Android o HAL costuma
    // recusar abrir a frontal enquanto a traseira ainda está aberta.
    pararStream(streamRef.current);
    streamRef.current = null;

    try {
      const stream = await abrirStream(facing);
      // Chegou atrasado (o usuário virou de novo / fechou): descarta em vez de
      // deixar mais um stream órfão preso no hardware.
      if (pedido !== pedidoRef.current) return pararStream(stream);

      streamRef.current = stream;
      // Espelhamento do preview E da captura seguem a câmera REAL entregue,
      // não a pedida — se o aparelho devolveu outra, a imagem não sai invertida.
      setFacingMode(facingDoStream(stream) || facing);
      ligarNoVideo(stream, pedido);
    } catch (err) {
      if (pedido !== pedidoRef.current) return;
      console.error("Camera error:", err);
      // Permissão negada ≠ câmera ocupada/inexistente: sem permissão, tentar
      // de novo não resolve (o sistema nem pergunta mais) — abre o guia.
      if (ehErroDePermissao(err)) {
        setPermissaoNegada(true);
      } else if (err?.name === "OverconstrainedError" || err?.name === "NotFoundError") {
        setError(
          facing === "user"
            ? "Não encontramos a câmera frontal neste aparelho."
            : "Não encontramos a câmera traseira neste aparelho."
        );
      } else {
        setError("Não foi possível acessar a câmera. Feche outros apps que possam estar usando a câmera e tente de novo.");
      }
    }
  }, [ligarNoVideo, pararPollPronto]);

  // monta câmera na abertura. Se recebeu um stream pré-aquecido (ScanFoodGuide
  // já ligou a câmera), adota ele e abre instantaneamente; senão, inicia normal.
  useEffect(() => {
    if (initialStream && initialStream.active) {
      const pedido = ++pedidoRef.current;
      streamRef.current = initialStream;
      facingRef.current = facingDoStream(initialStream) || "environment";
      setFacingMode(facingRef.current);
      ligarNoVideo(initialStream, pedido);
    } else {
      startCamera("environment");
    }
    return () => {
      // Invalida qualquer abertura em voo e solta o hardware.
      pedidoRef.current += 1;
      pararPollPronto();
      pararStream(streamRef.current);
      streamRef.current = null;
    };
  }, []);

  // retake: reconecta stream sem precisar reiniciar se ainda ativo
  useEffect(() => {
    // A primeira passada é a montagem — quem abre a câmera lá é o efeito
    // acima. Sem este pulo os dois disparavam getUserMedia ao mesmo tempo.
    if (puloPrimeiroRef.current) { puloPrimeiroRef.current = false; return; }
    if (preview !== null) return;
    const video = videoRef.current;
    if (!video) return;
    if (streamRef.current?.active) {
      ligarNoVideo(streamRef.current, pedidoRef.current);
    } else {
      startCamera(facingRef.current);
    }
  }, [preview, ligarNoVideo, startCamera]);

  // Trava o botão enquanto a troca acontece: dois toques rápidos abriam dois
  // getUserMedia concorrentes e voltavam a prender o hardware.
  const [trocandoCamera, setTrocandoCamera] = useState(false);

  const flipCamera = async () => {
    if (trocandoCamera) return;
    const next = facingRef.current === "environment" ? "user" : "environment";
    setTrocandoCamera(true);
    try { await startCamera(next); } finally { setTrocandoCamera(false); }
  };

  // ── captura ───────────────────────────────────────────────────────────────
  const capture = () => {
    if (!ready || capturing) return;
    setCapturing(true);
    setFlash(true);
    setTimeout(() => setFlash(false), 200);

    const video  = videoRef.current;
    const canvas = canvasRef.current;
    try {
      Object.assign(canvas, photoDimensions(video.videoWidth, video.videoHeight));
      const ctx = canvas.getContext("2d");
      if (facingMode === "user") { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = encodePhoto(canvas);

      // Foto de CÂMERA é tirada agora, por definição — o relógio do aparelho é a
      // fonte (ISO em UTC com "Z"; o offset explícito só importa no EXIF da galeria).
      metaCapturaRef.current = { origem: "camera", tiradaEm: new Date().toISOString() };

      setTimeout(() => { setCapturing(false); setPreview(dataUrl); }, 250);
    } catch {
      setCapturing(false);
      setError("Falha ao capturar a foto. Tente novamente.");
    }
  };

  // ── galeria ───────────────────────────────────────────────────────────────
  const openGallery = () => galleryInputRef.current?.click();

  const handleGalleryChange = (e) => {
    const file = e.target.files?.[0];
    // `e.target.value` é limpo LOGO no início: sem isso, escolher o MESMO
    // arquivo duas vezes seguidas não dispara onChange (o valor não mudou) e a
    // tela fica parada como se nada tivesse acontecido.
    e.target.value = "";
    if (!file) return;

    setError(null);

    // EXIF lido do File ORIGINAL (a compressão abaixo o descarta). Dispara já,
    // em paralelo com o decode; a promise NUNCA rejeita (falha → null).
    const exifPromise = lerTiradaEmDaGaleria(file);

    const reader = new FileReader();

    reader.onerror = () => {
      setError("Não foi possível ler essa imagem. Tenta escolher outra.");
    };

    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const img = new Image();

      // Sem isto, formato que o navegador não decodifica (HEIC em Android,
      // arquivo corrompido) deixava a tela em silêncio: o preview recebia um
      // dataURL que o <img> não renderiza e o usuário só via o vazio.
      img.onerror = () => {
        setError(
          "Não conseguimos abrir essa imagem. Tenta tirar a foto pela câmera " +
          "ou escolher outra da galeria."
        );
      };

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          Object.assign(canvas, photoDimensions(img.width, img.height));
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const redimensionada = encodePhoto(canvas);

          // ── Miniatura do botão da galeria ────────────────────────────────
          const tc = document.createElement("canvas");
          tc.width = tc.height = 80;
          const min = Math.min(img.width, img.height);
          const sx = (img.width - min) / 2;
          const sy = (img.height - min) / 2;
          tc.getContext("2d").drawImage(img, sx, sy, min, min, 0, 0, 80, 80);
          const thumb = tc.toDataURL("image/jpeg", 0.7);
          _lastGalleryThumb = thumb;
          setGalleryThumb(thumb);

          // Preview só entra DEPOIS de o EXIF resolver: garante que, quando o
          // usuário confirmar, o meta já está completo (parse é <50ms típico).
          exifPromise.then((tiradaEm) => {
            metaCapturaRef.current = { origem: "galeria", tiradaEm };
            setPreview(redimensionada);
          });
        } catch (err) {
          // toDataURL lança em imagem "suja" (cross-origin) e quando o canvas
          // estoura memória em aparelho fraco — os dois casos existem no campo.
          console.error("[CustomCamera] falha ao processar imagem da galeria:", err?.message);
          setError("Não foi possível processar essa imagem. Tenta outra.");
        }
      };

      img.src = dataUrl;
    };

    reader.readAsDataURL(file);
  };

  // ── review ────────────────────────────────────────────────────────────────
  // 2º argumento (meta) é ADITIVO: chamadores antigos, que só declaram
  // (dataUrl), o ignoram sem quebrar.
  const confirmCapture = () =>
    onCapture?.(preview, metaCapturaRef.current || { origem: null, tiradaEm: null });
  const retake = () => { metaCapturaRef.current = null; setPreview(null); };

  // ── render ────────────────────────────────────────────────────────────────
  const content = (
    <div className={Style.root}>

      {/*
        Video SEMPRE montado.
        - poster transparente: impede ícone de play
        - classe videoLoading: blur enquanto !ready (mostra frame anterior desfocado ou preto)
        - visibility hidden quando em review
      */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        poster={BLANK_POSTER}
        className={[
          Style.video,
          facingMode === "user" ? Style.mirrored : "",
          !ready ? Style.videoLoading : "",
        ].join(" ")}
        style={{ visibility: preview ? "hidden" : "visible" }}
      />
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Flash */}
      <div className={`${Style.flash} ${flash ? Style.active : ""}`} />

      {/* ── REVIEW ── */}
      {preview && (
        <div className={Style.reviewRoot}>
          <img src={preview} alt="Preview" className={Style.reviewImg} />
          <div className={Style.reviewVignette} />
          <div className={Style.reviewBar}>
            <button className={Style.reviewBtnX} onClick={retake} aria-label="Refazer">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </button>
            <button className={Style.reviewBtnOk} onClick={confirmCapture} aria-label="Confirmar">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M5 12l5 5L19 7" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── UI CÂMERA ── */}
      {!preview && (
        <>
          <div className={Style.vignetteTop} />
          <div className={Style.vignetteBottom} />

          {/* Top bar */}
          <div className={Style.topBar}>
            <button className={Style.iconBtn} onClick={onClose} aria-label="Fechar">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
            </button>
            <span className={Style.title}>
              {title || (isScanFood ? "Foto da Refeição" : "Foto do Treino")}
            </span>
            <div style={{ width: 40 }} />
          </div>

          {/* Frame de enquadramento (ScanFood por padrão). */}
          {(comMoldura ?? isScanFood) && (
            <div className={Style.frameWrapper}>
              <div className={Style.frame}>
                <span className={`${Style.corner} ${Style.tl}`} />
                <span className={`${Style.corner} ${Style.tr}`} />
                <span className={`${Style.corner} ${Style.bl}`} />
                <span className={`${Style.corner} ${Style.br}`} />
                <div className={Style.scanLineWrapper}>
                  <div className={Style.scanLine} />
                </div>
              </div>
              <p className={Style.frameHint}>{dica || "Posicione o prato dentro do quadro"}</p>
            </div>
          )}

          {slotModos}

          {/* Bottom bar */}
          <div className={Style.bottomBar}>
            {allowGallery ? (
              <button className={Style.galleryBtn} onClick={openGallery} aria-label="Abrir galeria">
                {galleryThumb ? (
                  <img src={galleryThumb} alt="Galeria" className={Style.galleryThumb} />
                ) : (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="3" stroke="white" strokeWidth="1.8"/>
                    <circle cx="8.5" cy="8.5" r="1.5" fill="white"/>
                    <path d="M21 15l-5-5L5 21" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            ) : (
              <span className={Style.galleryBtn} aria-hidden="true" style={{ visibility: "hidden" }} />
            )}

            <button
              className={`${Style.shutter} ${capturing ? Style.capturing : ""}`}
              onClick={capture}
              disabled={!ready}
              aria-label="Capturar foto"
            >
              <div className={Style.shutterInner} />
            </button>

            <button
              className={Style.iconBtnBottom}
              onClick={flipCamera}
              disabled={trocandoCamera}
              aria-label="Virar câmera"
              aria-busy={trocandoCamera}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M4 4v5h5M20 20v-5h-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L4 4m15.36 14.36A9 9 0 0 1 3.51 15H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Erro */}
          {error && (
            <div className={Style.errorOverlay}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ marginBottom: 12 }}>
                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
                <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p className={Style.errorText}>{error}</p>
              <button className={Style.retryBtn} onClick={() => startCamera(facingRef.current)}>
                Tentar novamente
              </button>
            </div>
          )}
        </>
      )}

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleGalleryChange}
      />

      {/* Permissão de câmera negada: passo a passo por plataforma + botão
          direto para as configurações do app. */}
      <PermissionHelpModal
        show={permissaoNegada}
        tipo="camera"
        onRetry={() => {
          setPermissaoNegada(false);
          startCamera(facingRef.current);
        }}
        onClose={() => {
          setPermissaoNegada(false);
          onClose?.();
        }}
      />
    </div>
  );

  return createPortal(content, document.body);
};

export default CustomCamera;
